import React, { useState, useEffect, useCallback } from 'react';
import { Button } from "@/ui-components/ui/button";
import { 
  Wand2, 
  Eye,
  ChevronRight,
  AlertCircle,
  Loader2,
  Layout,
  CalendarClock,
  X
} from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { PostGenerationResult } from '../services/PostGenerationService';
import { Link } from 'react-router-dom';
import { useWhatsappGroupsQuery } from '@/lib/api/requests/whatsapp.requests';
import type { PostPublishingOptions } from '../hooks/usePostPublishing';
import { useGenerateCaption } from '@/lib/api/requests/posts.requests';
import { useOrganizationsDepartmentAiCaptionSettingsQuery } from '@/lib/api/requests/organizations.requests';
import { IntegrationWarning } from './actions/IntegrationWarning';
import type { MetaTokenStatus } from '@/lib/api/interfaces/integrations.interface';
import { PlatformSelector } from './actions/PlatformSelector';
import { FormatSelector, PostFormat } from './actions/FormatSelector';
import { AccountSelector } from './actions/AccountSelector';
import { WhatsappGroupSelector, WhatsappGroup } from './actions/WhatsappGroupSelector';
import { CaptionInput } from './actions/CaptionInput';
import { SuccessState } from './actions/SuccessState';
import { SchedulePostDialog } from './actions/SchedulePostDialog';
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { usePermissions } from '@/lib/hooks/use-permissions';
import { toast } from '@/lib/toast';

// Types we need from the hooks
export interface IntegrationState {
  accounts: any[];
  isLoadingEntities: boolean;
  hasConnectedAccounts: boolean;
  tokenStatus: MetaTokenStatus;
  isTokenExpired: boolean;
  validateAccountSelection: (accountId: string | null, postToInstagram: boolean) => string | null;
}

export interface PublishingState {
  publishPost: (options: PostPublishingOptions) => Promise<void>;
  isPosting: boolean;
  isSuccess: boolean;
  reset: () => void;
}

interface PostActionsSectionProps {
  /** Current step in the generation process */
  currentStep: 'analysis' | 'selection' | 'preview' | 'generation';
  /** Whether generation is in progress */
  isGenerating: boolean;
  /** Generation result (contains the populated template) */
  generationResult: PostGenerationResult | null;
  /** The generated image URL (Blob URL) */
  generatedImageUrl?: string | null;
  /** Whether selection is complete */
  isSelectionComplete: boolean;
  /** Callback for proceeding to preview/generation */
  onProceedToPreview?: () => void;
  /** Callback for going back to selection */
  onBackToSelection?: () => void;
  /** Callback for the final "Post" action (optional local handler) */
  onGeneratePost?: () => void;
  /** Callback for downloading */
  onDownload?: () => void;
  /** Callback for sharing */
  onShare?: () => void;
  /** Validation warning message to display in the footer */
  validationWarning?: string | React.ReactNode;
  
  // Injected Dependencies
  integration: IntegrationState;
  publishing: PublishingState;

  /** Whether this is a custom image upload */
  isCustomUpload?: boolean;

  /** Draft/session identifier used for caption generation + usage tracking */
  draftSessionId: string;

  /** For testing/storybook: Initial scheduled date */
  initialScheduledDate?: Date;
}

/**
 * Professional Post Actions Component
 * Compact, efficient action controls with modern SaaS design
 */
export const PostActionsSection: React.FC<PostActionsSectionProps> = ({
  currentStep,
  isGenerating,
  generationResult,
  generatedImageUrl,
  isSelectionComplete,
  onProceedToPreview,
  onBackToSelection,
  onDownload,
  validationWarning,
  integration,
  publishing,
  isCustomUpload,
  draftSessionId,
  initialScheduledDate
}) => {
  const { can } = usePermissions();
  const canGenerateAiCaptions = can(Permission.GENERATE_AI_CAPTIONS);

  // Local state for configuration
  const [postToInstagram, setPostToInstagram] = useState<boolean>(false);
  const [postToFacebook, setPostToFacebook] = useState<boolean>(false);
  const [postToWhatsapp, setPostToWhatsapp] = useState<boolean>(false);
  const [selectedWhatsappGroupId, setSelectedWhatsappGroupId] = useState<string | null>(null);

  const [formatType, setFormatType] = useState<PostFormat>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [caption, setCaption] = useState<string>('');
  const [scheduledDate, setScheduledDate] = useState<Date | null>(initialScheduledDate || null);
  const [lastAutoCaptionKey, setLastAutoCaptionKey] = useState<string | null>(null);
  const [captionGenerationCount, setCaptionGenerationCount] = useState<number>(0);
  const [remainingCaptionRegenerations, setRemainingCaptionRegenerations] = useState<number>(3);
  const [remainingCaptionImprovements, setRemainingCaptionImprovements] = useState<number>(3);
  const [captionActionInProgress, setCaptionActionInProgress] = useState<'GENERATE' | 'IMPROVE' | null>(null);
  
  const { data: whatsappGroupsData, isLoading: isLoadingWhatsappGroups } = useWhatsappGroupsQuery();
  const whatsappGroups = React.useMemo<WhatsappGroup[]>(() => whatsappGroupsData?.groups || [], [whatsappGroupsData]);
  const hasWhatsappGroups = whatsappGroups.length > 0;
  const { data: aiCaptionSettings, isFetched: hasLoadedAiCaptionSettings } = useOrganizationsDepartmentAiCaptionSettingsQuery({
    enabled: canGenerateAiCaptions,
    retry: false,
  });
  const { mutateAsync: generateCaption, isPending: isGeneratingCaption } = useGenerateCaption();

  const { accounts, isLoadingEntities, hasConnectedAccounts, tokenStatus, isTokenExpired, validateAccountSelection } = integration;
  const { publishPost, isPosting, isSuccess, reset: resetPublishing } = publishing;

  // Reset state when entering selection step
  useEffect(() => {
    if (currentStep === 'selection') {
      setPostToInstagram(false);
      setPostToFacebook(false);
      setPostToWhatsapp(false);
      setSelectedWhatsappGroupId(null);
      setFormatType(null);
      setSelectedAccountId(null);
      setCaption('');
      setScheduledDate(null);
      setLastAutoCaptionKey(null);
      setCaptionGenerationCount(0);
      setRemainingCaptionRegenerations(3);
      setRemainingCaptionImprovements(3);
      setCaptionActionInProgress(null);
      resetPublishing();
    }
  }, [currentStep, resetPublishing]);

  // Validate selected account when platform selection changes
  useEffect(() => {
    if (selectedAccountId) {
       const validatedId = validateAccountSelection(selectedAccountId, postToInstagram);
       if (validatedId === null) {
          setSelectedAccountId(null);
       }
    }
  }, [postToInstagram, selectedAccountId, validateAccountSelection]);
  
  // Derived state
  const hasMetaSelection = (postToInstagram || postToFacebook) && !!selectedAccountId;
  const hasWhatsappSelection = postToWhatsapp && !!selectedWhatsappGroupId;
  const onlyWhatsapp = postToWhatsapp && !postToInstagram && !postToFacebook;
  const isReadyToPost = ((hasMetaSelection && formatType) || hasWhatsappSelection) && generatedImageUrl && !isGenerating && !isPosting && !isTokenExpired;
  const selectedPlatforms = [
    postToInstagram ? (formatType === 'story' ? 'instagram_story' : 'instagram_post') : null,
    postToFacebook ? (formatType === 'story' ? 'facebook_story' : 'facebook_post') : null,
    postToWhatsapp ? 'whatsapp' : null,
  ].filter((value): value is string => Boolean(value));

  // Reset format when only WhatsApp is selected (format is irrelevant for WhatsApp)
  useEffect(() => {
    if (onlyWhatsapp && formatType !== null) {
      setFormatType(null);
    }
  }, [onlyWhatsapp, formatType]);

  /**
   * Handles the actual posting process
   */
  const handlePost = async () => {
    await publishPost({
      draftSessionId,
      accountId: selectedAccountId,
      postToInstagram,
      postToFacebook,
      postToWhatsapp,
      whatsappGroupId: selectedWhatsappGroupId,
      format: formatType,
      caption,
      generatedImageUrl,
      generationResult,
      scheduledFor: scheduledDate
    });
  };

  const handleCaptionAction = useCallback(async (mode: 'GENERATE' | 'IMPROVE', autoTriggered = false) => {
    if (!canGenerateAiCaptions) {
      return;
    }

    if (mode === 'GENERATE' && remainingCaptionRegenerations <= 0) {
      if (!autoTriggered) {
        toast.error('Maximal 3 Unterschrift-Neu-Generierungen pro Post erreicht.');
      }
      return;
    }

    if (mode === 'IMPROVE') {
      if (!caption.trim()) {
        toast.error('Bitte zuerst eine bestehende Unterschrift eingeben oder generieren.');
        return;
      }

      if (remainingCaptionImprovements <= 0) {
        toast.error('Maximal 3 KI-Verbesserungen pro Post erreicht.');
        return;
      }
    }

    const template = generationResult?.populatedTemplate;

    try {
      setCaptionActionInProgress(mode);
      const response = await generateCaption({
        mode,
        draftSessionId,
        templateId: template?.id,
        templateName: template?.title || (isCustomUpload ? 'Eigene Grafik' : 'Social Media Post'),
        aspectRatio: template?.aspectRatio,
        format: formatType || 'post',
        selectedPlatforms,
        entities: generationResult?.entityContexts?.map((entity) => ({
          slotKey: entity.slotKey,
          entityType: entity.entityType,
          label: entity.label,
          data: entity.data,
        })) || [],
        textElements: generationResult?.textElements || [],
        autoTriggered,
        isCustomUpload,
        sourceCaption: mode === 'IMPROVE' ? caption : undefined,
      });

      setCaption(response.caption);
      setCaptionGenerationCount(response.generationCount);
      setRemainingCaptionRegenerations(response.remainingRegenerations);
      setRemainingCaptionImprovements(response.remainingImprovements);

      if (!autoTriggered) {
        if (mode === 'IMPROVE') {
          toast.success('Unterschrift wurde verbessert.');
        } else {
          toast.success(caption.trim() ? 'Unterschrift wurde neu generiert.' : 'Unterschrift wurde generiert.');
        }
      }
    } catch (error) {
      if (!autoTriggered) {
        toast.error(mode === 'IMPROVE' ? 'Unterschrift konnte nicht verbessert werden.' : 'Unterschrift konnte nicht generiert werden.', { error });
      }
    } finally {
      setCaptionActionInProgress(null);
    }
  }, [
    canGenerateAiCaptions,
    caption,
    draftSessionId,
    formatType,
    generateCaption,
    generationResult,
    isCustomUpload,
    remainingCaptionImprovements,
    remainingCaptionRegenerations,
    selectedPlatforms,
  ]);

  const handleReset = () => {
    resetPublishing();
    onBackToSelection?.();
  };

  useEffect(() => {
    const autoEnabled = aiCaptionSettings?.enabled === true;
    const autoCaptionKey = [
      draftSessionId,
      generationResult?.populatedTemplate?.id || 'custom-upload',
      generatedImageUrl || 'no-image',
    ].join(':');

    if (
      !hasLoadedAiCaptionSettings ||
      !autoEnabled ||
      !canGenerateAiCaptions ||
      !generatedImageUrl ||
      isGeneratingCaption ||
      currentStep === 'selection' ||
      lastAutoCaptionKey === autoCaptionKey
    ) {
      return;
    }

    setLastAutoCaptionKey(autoCaptionKey);
    void handleCaptionAction('GENERATE', true);
  }, [
    aiCaptionSettings?.enabled,
    canGenerateAiCaptions,
    currentStep,
    draftSessionId,
    generatedImageUrl,
    generationResult?.populatedTemplate?.id,
    handleCaptionAction,
    hasLoadedAiCaptionSettings,
    isGeneratingCaption,
    lastAutoCaptionKey,
  ]);

  // Render content based on step
  const renderContent = () => {
    switch (currentStep) {
      case 'analysis':
      case 'selection': // Show "Next" button logic
        return (
          <div className="text-center py-6 sm:py-8">
            <div className={`
              flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-lg border mx-auto mb-3 sm:mb-4
              ${currentStep === 'analysis' ? 'border-gray-200 bg-gray-100' : 'border-gray-200 bg-gray-50'}
            `}>
              {currentStep === 'analysis' ? (
                <Wand2 className="h-5 w-5 sm:h-6 sm:w-6 text-gray-500 animate-pulse" />
              ) : (
                <Eye className="h-5 w-5 sm:h-6 sm:w-6 text-gray-700" />
              )}
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
              {currentStep === 'analysis' ? 'Template wird analysiert' : 'Inhalte auswählen'}
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">
              {currentStep === 'analysis' 
                ? 'Benötigte Inhalte werden ermittelt...' 
                : 'Wählen Sie alle benötigten Inhalte aus.'}
            </p>
            
            {currentStep === 'selection' && (
              <div className="space-y-3 w-full sm:w-auto">
                {validationWarning && (
                  <div className="bg-orange-50 border border-orange-100 rounded-lg p-3 text-xs text-orange-700 flex items-center gap-2 max-w-xs mx-auto text-left">
                    <AlertCircle className="h-4 w-4 flex-shrink-0 text-orange-500" />
                    <span>{validationWarning}</span>
                  </div>
                )}
                {isSelectionComplete && (
                  <Button 
                    onClick={onProceedToPreview}
                    className="h-9 sm:h-11 px-4 sm:px-6 bg-gray-900 text-white hover:bg-gray-800 text-xs sm:text-sm font-medium shadow-sm transition-all w-full"
                  >
                    <span className="hidden sm:inline">Weiter zur Vorschau</span>
                    <span className="sm:hidden">Weiter</span>
                    <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 ml-1 sm:ml-2" />
                  </Button>
                )}
              </div>
            )}
          </div>
        );

      case 'preview': // Configuration Step
      case 'generation': // "Posting" or "Generating Image" state
        
        // Success State
        if (isSuccess) {
          return <SuccessState onReset={handleReset} scheduledDate={scheduledDate} />;
        }

        // While generating the image initially
        if (isGenerating && !generatedImageUrl) {
           return (
             <div className="text-center py-12">
               <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 border border-blue-100 mx-auto mb-4">
                 <Wand2 className="h-6 w-6 text-blue-600 animate-spin" />
               </div>
               <h3 className="text-lg font-semibold text-gray-900 mb-2">Bild wird erstellt...</h3>
               <p className="text-sm text-gray-500">Das Design wird finalisiert.</p>
             </div>
           );
        }

        return (
          <div className="space-y-6 animate-in fade-in duration-300">

            {/* Integration Warning */}
            {!isLoadingEntities && (tokenStatus === 'expired' || tokenStatus === 'expiring_soon' || !hasConnectedAccounts) && (
              <IntegrationWarning tokenStatus={tokenStatus} />
            )}

            {/* Platform Selection */}
            <div data-onboarding="create-post-platforms">
              <PlatformSelector
                postToInstagram={postToInstagram}
                setPostToInstagram={setPostToInstagram}
                postToFacebook={postToFacebook}
                setPostToFacebook={setPostToFacebook}
                postToWhatsapp={postToWhatsapp}
                setPostToWhatsapp={setPostToWhatsapp}
                hasConnectedAccounts={hasConnectedAccounts}
                hasWhatsappGroups={hasWhatsappGroups}
              />
            </div>

            {/* WhatsApp Integration Warning */}
            {(postToWhatsapp && whatsappGroups.length === 0 && !isLoadingWhatsappGroups) && (
              <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-100 rounded-lg p-4 mt-3">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                   <AlertCircle className="h-4 w-4 text-orange-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-sm font-medium text-orange-900">Keine WhatsApp-Gruppen gefunden</h4>
                    </div>
                    <p className="text-sm text-orange-700 leading-relaxed">
                      Bitte richten Sie die WhatsApp-Integration <Link to="/integrations" className="underline">in den Einstellungen</Link> ein.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Format Selection - only relevant when Meta platforms are selected */}
            {(postToInstagram || postToFacebook) && (
              <FormatSelector format={formatType} setFormat={setFormatType} />
            )}

            {/* Account Selection */}
            {(postToInstagram || postToFacebook) && (
              <AccountSelector
                accounts={accounts}
                isLoading={isLoadingEntities}
                selectedAccountId={selectedAccountId}
                onSelectAccount={setSelectedAccountId}
                postToInstagram={postToInstagram}
                hasConnectedAccounts={hasConnectedAccounts}
              />
            )}

            {/* WhatsApp Group Selection */}
            {postToWhatsapp && (
              <WhatsappGroupSelector
                groups={whatsappGroups}
                isLoading={isLoadingWhatsappGroups}
                selectedGroupId={selectedWhatsappGroupId}
                onSelectGroup={setSelectedWhatsappGroupId}
              />
            )}

            {/* Caption Input */}
            <CaptionInput
              caption={caption}
              setCaption={setCaption}
              isGeneratingCaption={isGeneratingCaption}
              onGenerateCaption={() => void handleCaptionAction('GENERATE', false)}
              onImproveCaption={() => void handleCaptionAction('IMPROVE', false)}
              canGenerateAiCaptions={canGenerateAiCaptions}
              generationCount={captionGenerationCount}
              remainingRegenerations={remainingCaptionRegenerations}
              remainingImprovements={remainingCaptionImprovements}
              actionInProgress={captionActionInProgress}
            />
            
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white overflow-hidden lg:h-full lg:flex lg:flex-col lg:overflow-hidden">
      {/* Header - Hide in success state */}
      {((currentStep === 'preview' || currentStep === 'generation') && !isSuccess) && (
        <div className="px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="flex h-6 w-6 sm:h-8 sm:w-8 items-center justify-center rounded-lg bg-gray-100 border border-gray-200">
              <Layout className="h-3 w-3 sm:h-4 sm:w-4 text-gray-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">Beitrag erstellen</h3>
              </div>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Konfigurieren Sie Ihren Post für Social Media</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {renderContent()}
      </div>

      {/* Footer Actions - Hide in success state */}
      {((currentStep === 'preview' || currentStep === 'generation') && !isSuccess) && (
        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-white border-t border-gray-100 lg:flex-none pb-24 lg:pb-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <PermissionFeatureWrapper
                requiredPermission={Permission.MANAGE_ENTITIES}
                requiredPlan={PlanKey.BASIC}
              >
                <Button
                  onClick={handlePost}
                  disabled={!isReadyToPost}
                  data-onboarding="create-post-submit"
                  className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 text-white shadow-md rounded-xl font-medium text-base transition-all disabled:opacity-50 disabled:shadow-none"
                >
                  {isPosting ? (
                    <>
                       <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                       {scheduledDate ? 'Wird eingeplant...' : 'Wird veröffentlicht...'}
                    </>
                  ) : (
                    scheduledDate ? 'Post einplanen' : 'Post veröffentlichen'
                  )}
                </Button>
              </PermissionFeatureWrapper>
              <SchedulePostDialog
                initialDate={scheduledDate || undefined}
                onSave={setScheduledDate}
                trigger={
                  <div data-onboarding="create-post-schedule">
                    <PermissionFeatureWrapper
                      requiredPermission={Permission.MANAGE_ENTITIES}
                      requiredPlan={PlanKey.BASIC}
                    >
                      <Button
                        disabled={!isReadyToPost}
                        variant={scheduledDate ? "secondary" : "outline"}
                        className={`h-12 transition-all duration-300 rounded-xl flex-shrink-0 ${scheduledDate ? 'px-4 bg-blue-50 border border-blue-200 text-blue-800 hover:bg-blue-100 shadow-sm' : 'w-12 p-0 border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                        title={scheduledDate ? "Planung ändern" : "Post planen"}
                      >
                        <CalendarClock className={`h-5 w-5 ${scheduledDate ? 'mr-2 text-blue-600' : ''}`} />
                        {scheduledDate && (
                          <div className="flex flex-col items-start text-left mr-1">
                            <span className="text-[9px] uppercase font-bold text-blue-500 tracking-widest leading-tight">Geplant für</span>
                            <span className="text-xs font-bold leading-tight">{format(scheduledDate, "dd.MM. - HH:mm", { locale: de })}</span>
                          </div>
                        )}
                      </Button>
                    </PermissionFeatureWrapper>
                  </div>
                }
              />
              {scheduledDate && (
                 <Button
                    variant="ghost"
                    onClick={() => setScheduledDate(null)}
                    className="h-12 w-10 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl flex-shrink-0 -ml-1 transition-colors"
                    title="Planung entfernen"
                 >
                   <X className="h-5 w-5" />
                 </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
